import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {getUser} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerTextPair} from '../../../../../libs/shared/ui/src/components/banners/bannerTextPair';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {teammateCanAccessInbox} from '../../../../helpers/modelHelpers';
import {MacroExecutionInfo} from '../../../../models/api/macroModel';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {navigateToConversation} from '../../../../store/stateless/navigationActions';

/*
 * Props.
 */

interface MacrosSuccessBannerProps {
  macroExecutionInfo: Omit<MacroExecutionInfo, 'namespace' | 'macroId'>;
  onDismissClick: () => void;
}

/*
 * Component.
 */

export const MacrosSuccessBanner: FC<MacrosSuccessBannerProps> = ({macroExecutionInfo, onDismissClick}) => {
  const {name, conversation} = macroExecutionInfo;
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const allInboxes = useAppSelector(getInboxes);

  const userHasAccessToSomeNewInbox = useMemo(
    () =>
      conversation.newInboxes.some((inboxId) => {
        const inbox = allInboxes[inboxId];
        if (!inbox) {
          return false;
        }
        return teammateCanAccessInbox(user, inbox);
      }),
    [allInboxes, conversation.newInboxes, user],
  );

  const userHasAccessThroughLiveTrackers = conversation.newLiveTrackers.includes(user.id);

  const shouldRenderViewButton = userHasAccessToSomeNewInbox || userHasAccessThroughLiveTrackers;

  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.TRANSIENT}>
      <BannerIcon iconName="lightningFilledSmall" />
      <BannerTextPair
        leftText={name}
        rightText={<Trans id="macros-success-banner-complete">complete</Trans>}
      />
      {shouldRenderViewButton ? (
        <BannerAction onClick={() => dispatch(navigateToConversation(conversation.id))}>
          <Trans id="banner-view">View</Trans>
        </BannerAction>
      ) : (
        <BannerCloseAction onClick={onDismissClick} />
      )}
    </Banner>
  );
};
